<template>
  <div>
    <div
      v-if="isLiveRampOptedOut"
      class="message-bar"
    >
      <MessageBarV02
        color="#223448"
        dark
        dense
        dismissible
        transition="fade-transition"
        tile
      >
        {{ liveRampOffMessage }}
      </MessageBarV02>
    </div>
    <div v-if="isDemographicsEnabled">
      <DemographicsHeader
        :dateValue="dateValue"
        :minDate="minDate"
        :maxDate="maxDate"
        :canDownloadReport="canDownloadReport"
        @downloadClick="downloadData"
        @filtersEdit="openDimensionsMetricsTab"
        @dateApply="handleDateApply"
      ></DemographicsHeader>
      <DemographicsContent
        :isNewReport="showNewReportMessage"
        :hasThresholdError="pageviewThresholdError"
        :hasNoResultsMessage="hasNoResultsMessage"
        :portfolioBrands="demographicsEnabledPortfolioBrands"
        :selectedFilters="selectedFilters"
        :data="demographicsData"
        :loading="dashboardLoading"
        :dimension="dimension"
        :metrics="metrics"
        :summaryRow="summaryRow"
        @openSlideOut="showSlideOut = true"
        @add="addFilters($event)"
        @complete="runDemographicsReport"
        @runAllSegments="runAllSegments"
      />
      <DemographicsSlideOut
        :show="showSlideOut"
        v-bind:slideOutTab="slideOutTab"
        :portfolioBrands="demographicsEnabledPortfolioBrands"
        :selectedFilters="selectedFilters"
        :selectedMetrics="metrics"
        :selectedDimension="dimension"
        :includeFullSegments="includeFullSegments"
        :selectedFilterType="filterType"
        :newReportId="newReportId"
        @offNewReportId="offNewReportId"
        @handleTabClick="handleTabClick($event)"
        @cancel="showSlideOut = false"
        @complete="applyFilters"
      ></DemographicsSlideOut>
    </div>
    <ChartBlock v-else>
      <ReportMessage
        class="inactive-report"
        :message="inactiveDemographicsMessage"
      />
    </ChartBlock>
    <OnboardingTour
      :tourSteps="shopperIntelligenceTourSteps"
      tourName="Shopper Intelligence"
    ></OnboardingTour>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import { getSelectedPortfolioBrands } from '../../../services/dashboard/portfolioBrandsService';
import { getDemographicsReport, getDemographicsAllSegmentsReport }
  from '../../../services/dashboard/eventApiService';
import {
  createDashboardReport,
  getDashboardReport,
} from '../../../services/dashboard/dashboardReportService';
import overlayMixin from '../../../mixins/loadingOverlay';
import DemographicsContent from '../Demographics/DemographicsContent';
import DemographicsHeader from '../Demographics/DemographicsHeader';
import DemographicsSlideOut from '../Demographics/SlideOut/DemographicsSlideOut';
import OnboardingTour from './OnboardingTour';
import ReportMessage from '../Common/ReportMessage';
import ChartBlock from '../Common/Charting/Shared/ChartBlock.vue';
import { downloadTableData } from '../../../services/dashboard/downloadService';
import { getNormalizedMetricValues } from '../../../services/dashboard/exclusionsService';
import {
  getReportFieldFromSnake,
  getReportFieldFromValue,
  getReportFieldsFromValues,
  getReportFieldSnakeFromValue,
  getDemographicsFieldSnake,
} from '../../../services/dashboard/reportFieldService';
import { shopperIntelligenceTourSteps } from './config/onboardingTourSteps';
import eventDispatcher from '../../../events/dispatch';
import { getLocalDateFromUnixTimestamp } from '../../../services/dashboard/dates/datesService';
import {
  getFilterSelectionsTotalCount,
  getErrorMessage,
} from '../../../services/dashboard/dashboardUtils';
import {
  getSelectedFilters,
  demographicsAllowedFilters,
} from '../../../services/dashboard/filtersService';
import {
  rateDisplayCsvGetter,
  nullDisplayCsvGetter,
} from '../../../services/dashboard/displayFormatService';
import { MessageBarV02 } from '@mikmak/components';
import { dashboardMessages } from './config/dashboardMessages';

export default {
  mixins: [ overlayMixin ],
  props: {
    minDate: Date,
    maxDate: Date,
    isComingFromInsightsCard: Boolean,
    dateValue: Object,
    startDate: Date,
    endDate: Date,
    startDateStr: String,
    endDateStr: String,
    isValidDateRange: Boolean,
    brandIds: Array,
    brandNames: Array,
    brandIdsStr: String,
    encodedBrandIds: String,
  },
  components: {
    DemographicsHeader,
    DemographicsContent,
    DemographicsSlideOut,
    OnboardingTour,
    ReportMessage,
    ChartBlock,
    MessageBarV02,
  },
  data() {
    return {
      viewName: 'Shopper Intelligence',
      inactiveDemographicsMessage: {
        title: 'Hi there! We\'re excited about Shopper Intelligence too.',
        description: ['Contact your MikMak representative ' +
          'to learn more about the value of tying demographic/psychographic data ' +
          'to your MikMak Insights.',
        ],
      },
      showSlideOut: false,
      dashboardError: false,
      dashboardLoading: false,
      slideOutTab: 0,
      shopperIntelligenceTourSteps: shopperIntelligenceTourSteps,
      newReportId: false,
      liveRampOffMessage: dashboardMessages.liveRampOff,
    };
  },
  computed: {
    ...mapState({
      isLoadingOverlayVisible: state => state.main.loadingOverlayVisible,
      portfolioBrands: state => state.dashboard.portfolioBrands,
      filters: state => state.dashboard.reportRequest.demographicsFilters,
      metrics: state => state.dashboard.reportRequest.demographicsMetrics,
      dimension: state => state.dashboard.reportRequest.demographicsDimension,
      includeFullSegments: state => state.dashboard.reportRequest.demographicsIncludeFullSegments,
      currentDemographicsReportId: state => state.dashboard.currentDemographicsReportId,
      demographicsData: state => state.dashboard.demographicsData,
      filterType: state => state.dashboard.reportRequest.demographicsFilterType,
      currentUser: state => state.main.currentUser,
      allSegments: state => state.dashboard.availableReportFields.allSegmentsDimension,
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
      dashboardSelectedFilters: state => state.filters.dashboardSelections.filters || [],
      dimensions: state => state.dashboard.availableReportFields.dimensions,
      demographicsDimensions: state => state.dashboard.availableReportFields.demographicsDimensions,
      allowedFilters: state => state.filters.allowedFilters.demographics,
      allowedInsightsFilters: state => state.filters.allowedFilters.insights || [],
    }),
    ...mapGetters([
      'demographicsEnabledPortfolioBrands',
      'selectedBrands',
    ]),
    selectedFilters() {
      if (this.filterType === 'Basic') {
        const filters = getSelectedFilters(
          this.dashboardSelectedFilters,
          this.allowedFilters,
        );

        return [{ filters }];

      } else {
        return this.filters;
      }
    },
    currentUserEmail() {
      return this.currentUser && this.currentUser.email;
    },
    canDownloadReport() {
      return !!(this.tableRows && this.tableRows.length &&
        this.tableColumns && this.tableColumns.length);
    },
    tableRows() {
      if (this.demographicsData && this.demographicsData.rows) {
        return this.demographicsData.rows;
      }

      return [];
    },
    tableColumns() {
      if (!this.tableRows || !this.tableRows.length || !this.tableRows[0]) {
        return [];
      }

      const columns = Object.keys(this.tableRows[0]);

      if (!columns || !columns.length) {
        return [];
      }

      return this.isAllSegmentsReport
        ? [
          'full_segment_name',
          ...this.selectedMetricsSnakes,
        ]
        : columns;
    },
    downloadedReportColumns() {
      return this.tableColumns.map(column => {
        const reportParameter = getReportFieldFromSnake(this.allReportFields, column);

        const columnName = reportParameter
          ? getDemographicsFieldSnake(reportParameter)
          : column;

        const isRateType = reportParameter && reportParameter.type === 'rate';
        const isDemographicsParameter = reportParameter && reportParameter.groups.includes('demographics');

        let columnValue = column;

        if (isRateType) columnValue = rateDisplayCsvGetter(column);
        else if (isDemographicsParameter) columnValue = nullDisplayCsvGetter(column);

        return {
          label: columnName,
          value: columnValue,
        };
      });
    },
    summaryRow() {
      return this.demographicsData?.summary;
    },
    isValidReportRequest() {
      return this.isValidDateRange
        && this.brandIds
        && this.brandIds.length >= 1
        && this.hasSelectedDimension
        && this.hasSelectedMetrics;
    },
    isAllSegmentsReport() {
      return this.dimension === this.allSegments.value;
    },
    hasSelectedDimension() {
      return this.dimension && (
        this.isAllSegmentsReport ||
        !!getReportFieldFromValue(this.allReportFields, this.dimension)
      );
    },
    hasSelectedMetrics() {
      const metricsParameters = getReportFieldsFromValues(this.allReportFields, this.metrics);

      return metricsParameters && metricsParameters.length > 0;
    },
    showNewReportMessage() {
      return !this.demographicsData && !this.dashboardLoading;
    },
    hasNoResultsMessage() {
      return this.demographicsData && this.demographicsData.rows
        ? this.demographicsData.rows.length === 0
        : false;
    },
    pageviewThresholdError() {
      return this.demographicsData
        ? !this.demographicsData.isStatisticallySignificant
        : false;
    },
    isDemographicsEnabled() {
      return this.demographicsEnabledPortfolioBrands.length > 0;
    },
    isLiveRampOptedOut() {
      return this.selectedBrands.every(pb => pb.optOutLiveRamp);
    },
    brandNamesStr() {
      return this.brandNames.join(', ');
    },
    metricsStr() {
      return this.metrics.join(', ');
    },
    selectedMetricsSnakes() {
      return this.metrics.map(metric => getReportFieldSnakeFromValue(this.allReportFields, metric));
    },
    stringifiedFilters() {
      return JSON.stringify(this.selectedFilters);
    },
    filterTotalCount() {
      if (!this.selectedFilters) return 0;

      return this.selectedFilters.reduce((acc, group) => {
        acc += group.filters.length;

        return acc;
      }, 0);
    },
    filterGroupsCount() {
      if (!this.selectedFilters) return 0;

      return this.selectedFilters
        .filter((group) => group.filters && group.filters.length > 0).length;
    },
    filterDimensions() {
      if (!this.selectedFilters) return [];

      const dimensionsObject = this.selectedFilters.reduce((acc, group) => {
        for (const filter of group.filters) {
          acc.add(filter.key);
        }

        return acc;
      }, new Set());

      return [...dimensionsObject];
    },
    filterSelectionsTotalCount() {
      if (!this.selectedFilters) return 0;

      return this.selectedFilters.reduce((acc, group) => {
        const innerFilters = group.filters ? group.filters : [];

        acc = acc + getFilterSelectionsTotalCount(innerFilters);

        return acc;
      }, 0);
    },
    analyticsObj() {
      return {
        dashboard_view: this.viewName,
        start_date: this.startDateStr,
        end_date: this.endDateStr,
        brand_ids: this.brandIdsStr,
        brand_names: this.brandNamesStr,
        brand_count: this.brandIds.length,
        dimension: this.dimension,
        metrics: this.metricsStr,
        metric_count: this.metrics.length,
        full_segments: this.includeFullSegments,
        filters: this.stringifiedFilters,
        filter_groups_count: this.filterGroupsCount,
        filter_total_count: this.filterTotalCount,
        filter_selections_total_count: this.filterSelectionsTotalCount,
        filter_stringified_length: this.stringifiedFilters.length,
        filter_type: this.filterType,
        filter_dimensions: this.filterDimensions.join(', '),
        filter_dimensions_count: this.filterDimensions.length,
      };
    },
  },
  methods: {
    downloadData() {
      const rowData = cloneDeep(this.tableRows);

      let downloadError = false;
      let fileName = undefined;
      let fileSize = undefined;

      try {
        const downloadObject = downloadTableData(this.downloadedReportColumns, rowData);

        fileName = downloadObject.name;
        fileSize = downloadObject.size;
      } catch (err) {
        this.$emit('showMessage', { type: 'csvDownloadError' });

        downloadError = true;
      } finally {
        this.dispatchDemographicsDownloadAnalyticsEvent(downloadError, fileName, fileSize);
      }
    },
    offNewReportId() {
      this.newReportId = false;
    },
    handleTabClick(tab) {
      this.slideOutTab = tab;
    },
    openDimensionsMetricsTab() {
      this.slideOutTab = 0;
      this.showSlideOut = true;
    },
    addFilters() {
      this.slideOutTab = 1;
      this.showSlideOut = true;
    },
    handleDateApply(value) {
      const { predefinedRangeValue, range } = value;

      this.$store.commit('updatePredefinedRangeValue', predefinedRangeValue);

      this.$store.commit('updateReportEndDate', range.end);
      this.$store.commit('updateReportStartDate', range.start);

      if (!this.isValidReportRequest) {
        return;
      }

      this.$nextTick(() => {
        this.runDemographicsReport();
      });
    },
    runAllSegments() {
      this.$store.commit(
        'updateDemographicsDimension',
        this.allSegments.value,
      );
      this.$store.commit('updateDemographicsIncludeFullSegments', true);

      if (!this.isValidReportRequest) {
        return;
      }

      this.runDemographicsReport();
      this.newReportId = true;
    },
    async applyFilters(newReportFields) {

      const {
        brands,
        dimension,
        metrics,
        filters,
        includeFullSegments,
        filterType,
      } = newReportFields;

      const metricValues = (metrics || []).map(m => m.value);

      this.$store.commit('updateDemographicsDimension', dimension);
      this.$store.commit('updateDemographicsMetrics', metricValues);
      this.$store.commit('updateDemographicsIncludeFullSegments', includeFullSegments);
      this.$store.commit('updateDemographicsFilterType', filterType);
      this.$store.commit('updateDemographicsAllowedFilters', demographicsAllowedFilters());

      if (this.filterType === 'Basic') {
        const basicFilters = filters[0]?.filters || [];

        this.$store.dispatch('setDashboardSelectedFilters', basicFilters);
      } else {
        this.$store.commit('updateDemographicsFilters', filters);
      }

      const filteredBrandIds = (brands || []).reduce((acc, brand) => {
        if (brand.value && brand.value.length) acc.push(brand.value);

        return acc;
      }, []);

      if (filteredBrandIds.length === 0) {
        return;
      }

      const updatedBrands = getSelectedPortfolioBrands(this.portfolioBrands, filteredBrandIds);

      await this.$store.dispatch('setPortfolioBrands', updatedBrands);

      this.showSlideOut = false;

      this.dispatchDemographicsRequestAnalyticsEvent();

      this.runDemographicsReport();
    },
    async runDemographicsReport() {
      this.clearCurrentDemographicsReportId();
      await this.getAndSetDemographicsData();
      this.createAndUpdateDemographicsReport();
    },
    clearCurrentDemographicsReportId() {
      if (this.currentDemographicsReportId) {
        this.updateCurrentDemographicsReportId(null);
      }
      this.$router.push({ name: 'demographics-base' });
    },
    async getAndSetDemographicsData() {
      if (!this.isValidReportRequest) {
        this.dashboardError = true;
        this.dashboardLoading = false;

        return;
      }

      this.dashboardError = false;
      this.dashboardLoading = true;
      let errorMessage = '';

      try {
        const data = await this.getDemographicsData();

        this.setDemographicsData(data);
      } catch (error) {
        this.$emit('showMessage', { type: 'dashboardError' });
        this.dashboardError = true;
        errorMessage = getErrorMessage(error);

        if (this.demographicsData) {
          this.$store.commit('updateDemographicsData', undefined);
        }
      } finally {
        this.dispatchDemographicsResponseAnalyticsEvent(this.dashboardError, errorMessage);
        this.dashboardLoading = false;
      }
    },
    async getDemographicsData() {

      const token = await this.$auth.getTokenSilently();

      const demographicsPayload = {
        brandIds: this.brandIds,
        metrics: this.metrics,
        filterGroups: this.selectedFilters,
        dateRange: {
          startDate: this.startDateStr,
          endDate: this.endDateStr,
        },
      };

      if (this.isAllSegmentsReport) {
        const { data } = await getDemographicsAllSegmentsReport(demographicsPayload, token);

        return data;
      } else {
        demographicsPayload['includeFullSegmentNames'] = this.includeFullSegments;
        demographicsPayload['dimension'] = this.dimension;

        const { data } = await getDemographicsReport(demographicsPayload, token);

        return data;
      }
    },
    setDemographicsData(data) {
      this.$store.commit('updateDemographicsData', data);
    },
    async createAndUpdateDemographicsReport() {

      try {
        this.dashboardLoading = true;

        const demographicsReportId = await this.createDemographicsReport();

        this.updateCurrentDemographicsReportId(demographicsReportId);

        if (demographicsReportId) {
          this.$router.push({
            name: 'demographics-id',
            params: { demographicsReportId },
          });
        }

      } catch (e) {
        this.dashboardError = true;
        this.$emit('showMessage', { type: 'permalinkSaveError' });
      } finally {
        this.dashboardLoading = false;
      }

    },
    async createDemographicsReport() {
      let createdDemographicsReportId = null;

      try {

        const payload = {
          view: this.viewName,
          startDate: this.startDateStr,
          endDate: this.endDateStr,
          brandIds: this.brandIds,
          dimensions: [this.dimension],
          metrics: this.metrics,
          filterGroups: this.selectedFilters,
          includeFullSegments: this.includeFullSegments,
          filtersType: this.filterType,
        };

        const token = await this.$auth.getTokenSilently();

        const { id } = await createDashboardReport(payload, token);

        createdDemographicsReportId = id;

        return id;
      } catch (e) {
        this.$emit('showMessage', { type: 'permalinkSaveError' });
      } finally {
        eventDispatcher('dashboardReportCreated', {
          ...this.analyticsObj,
          dashboard_report_id: createdDemographicsReportId,
          has_error: this.dashboardError,
        });
      }
    },
    updateCurrentDemographicsReportId(demographicsReportId) {
      this.$store.commit('updateCurrentDemographicsReportId', demographicsReportId);
    },
    async getAndSetDemographicsReport(demographicsReportId) {
      this.dashboardLoading = true;

      try {
        const { dashboardReport } = await this.retrieveDashboardReport(demographicsReportId);

        await this.setDashboardReportFields(dashboardReport);

        this.$nextTick(() => {
          this.getAndSetDemographicsData();
        });

        this.updateCurrentDemographicsReportId(demographicsReportId);
        this.newReportId = true;

      } catch (e) {

        this.dashboardError = true;
        this.clearCurrentDemographicsReportId();
        this.$emit('showMessage', { type: 'permalinkRetrievalError' });

      } finally {

        this.dashboardLoading = false;
        eventDispatcher('dashboardReportLoaded', {
          ...this.analyticsObj,
          dashboard_report_id: demographicsReportId,
          loaded_by: this.currentUserEmail,
          has_error: this.dashboardError,
        });

      }
    },
    async retrieveDashboardReport(demographicsReportId) {
      if (!demographicsReportId) {
        return null;
      }


      eventDispatcher('dashboardReportRequested', {
        dashboard_view: this.viewName,
        dashboard_report_id: demographicsReportId,
        requested_by: this.currentUserEmail,
      });

      try {
        const token = await this.$auth.getTokenSilently();

        return await getDashboardReport(demographicsReportId, token);
      } catch (e) {
        throw getErrorMessage(e);
      }
    },
    async setDashboardReportFields(dashboardReport) {

      if (!dashboardReport) {
        this.dashboardError = true;

        return;
      }

      this.dashboardLoading = true;
      const {
        view,
        brandIds,
        startDate,
        endDate,
        dimensions,
        metrics,
        filterGroups,
        filtersType,
        includeFullSegments,
      } = dashboardReport;

      if (!view || view !== this.viewName || !brandIds) {
        this.dashboardError = true;
      }

      const updatedBrands = getSelectedPortfolioBrands(this.portfolioBrands, brandIds);

      await this.$store.dispatch('setPortfolioBrands', updatedBrands);

      if (startDate) {
        const reportStartDate = getLocalDateFromUnixTimestamp(startDate);

        this.$store.commit('updateReportStartDate', reportStartDate);
      }

      if (endDate) {
        const reportEndDate = getLocalDateFromUnixTimestamp(endDate);

        this.$store.commit('updateReportEndDate', reportEndDate);
      }

      if (includeFullSegments) {
        this.$store.commit('updateDemographicsIncludeFullSegments', includeFullSegments);
      }

      this.$store.commit('updateDemographicsFilterType',
        filtersType ? filtersType : 'Basic');

      const dimension = dimensions && dimensions.length > 0 ? dimensions[0] : '';

      this.$store.commit('updateDemographicsDimension', dimension);
      this.$store.commit(
        'updateDemographicsMetrics',
        getNormalizedMetricValues(this.allReportFields, metrics),
      );
      this.$store.commit('updateDemographicsAllowedFilters', demographicsAllowedFilters());

      const cleanedFilters = this.arrayIfEmpty(filterGroups).map(filterObject => {

        const filtersArray = filterObject.filters;

        filtersArray.map(f => {
          if (f.value === undefined) {
            return { ...f, value: [null] };
          }

          if (Array.isArray(f.value) && f.value.some(v => v === undefined)) {
            return { ...f, value: f.value.map(v => v === undefined ? null : v) };
          }

          return f;
        });

        return filterObject;
      });

      if (this.filterType === 'Basic') {
        this.$store.dispatch('setDashboardSelectedFilters', cleanedFilters[0]?.filters || []);
      } else {
        this.$store.commit('updateDemographicsFilters', cleanedFilters);
      }
    },
    arrayIfEmpty(arr) {
      return arr ? arr : [];
    },
    stringifyFilterValue(filter) {
      return filter.join(', ');
    },
    filterExistsInArray(filter, filterGroups) {
      let filterExists = false;

      for (const group of filterGroups) {
        const filters = group.filters;
        const foundFilter = filters
          .find(f => {
            return f.key === filter.key;
          });

        if (foundFilter) filterExists = true;
      }

      return filterExists;
    },
    dispatchDemographicsRequestAnalyticsEvent() {

      eventDispatcher('dashboardRequest', {
        ...this.analyticsObj,
      });
    },
    dispatchDemographicsResponseAnalyticsEvent(hasError, errorMessage) {
      eventDispatcher('dashboardRequestResponse', {
        ...this.analyticsObj,
        has_error: hasError,
        error_message: errorMessage,
      });
    },
    dispatchDemographicsDownloadAnalyticsEvent(hasError, fileName, fileSize) {

      eventDispatcher('dashboardCSVDownloaded', {
        ...this.analyticsObj,
        has_error: hasError,
        file_name: fileName,
        file_size: fileSize,
      });
    },
  },
  watch: {
    $route(to, _from) {
      const demographicsReportId = to.params.demographicsReportId;

      if (demographicsReportId && demographicsReportId !== this.currentDemographicsReportId) {
        this.getAndSetDemographicsReport(demographicsReportId);
      }
    },
  },
  beforeMount() {
    this.$store.commit('updateActiveView', this.viewName);
  },
  async mounted() {
    if (this.isLoadingOverlayVisible) {
      this.hideLoadingOverlay();
    }

    const demographicsReportId = this.$route.params.demographicsReportId;

    if (demographicsReportId && demographicsReportId !== this.currentDemographicsReportId) {
      await this.getAndSetDemographicsReport(demographicsReportId);
    } else if (demographicsReportId) {
      this.newReportId = true;
    } else if (this.isComingFromInsightsCard) {
      this.$store.commit(
        'updateDemographicsIncludeFullSegments',
        this.isAllSegmentsReport,
      );
      await this.getAndSetDemographicsData();
      this.createAndUpdateDemographicsReport();
      this.$emit('resetIsComingFromInsightsCard');
    } else if (this.demographicsData && this.currentDemographicsReportId) {
      this.setDemographicsData(this.demographicsData);
      this.$router.push({
        name: 'demographics-id',
        params: { demographicsReportId: this.currentDemographicsReportId },
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.message-bar {
  width: calc(100% + 48px);
  margin-left: -24px;
}
</style>
